/**
 * XP & Levelling System
 *
 * XP awarded per session, scaled by duration with bonus for longer sessions.
 * Levels follow a gentle exponential curve.
 */

/**
 * Calculate XP earned for a completed session
 */
export function calculateSessionXP(durationMinutes) {
  // Base XP = 1 per minute
  // Bonus multiplier for longer sessions (encourages deep work)
  let multiplier = 1;
  if (durationMinutes >= 90) multiplier = 1.5;
  else if (durationMinutes >= 50) multiplier = 1.3;
  else if (durationMinutes >= 25) multiplier = 1.1;

  return Math.round(durationMinutes * multiplier);
}

/**
 * XP required to reach a given level
 * Level 1 = 0 XP, Level 2 = 50 XP, escalates gently
 */
export function xpForLevel(level) {
  if (level <= 1) return 0;
  // Quadratic growth: 50 * (level-1)^1.5
  return Math.round(50 * Math.pow(level - 1, 1.5));
}

/**
 * Given total XP, calculate current level and progress to next
 */
export function calculateLevel(totalXP) {
  let level = 1;
  while (xpForLevel(level + 1) <= totalXP) {
    level++;
  }

  const currentLevelXP = xpForLevel(level);
  const nextLevelXP = xpForLevel(level + 1);
  const progressXP = totalXP - currentLevelXP;
  const requiredXP = nextLevelXP - currentLevelXP;
  const progress = requiredXP > 0 ? progressXP / requiredXP : 0;

  return { level, totalXP, progressXP, requiredXP, progress };
}

/**
 * Streak milestone rewards
 */
export const STREAK_MILESTONES = [
  { days: 3, reward: 'Bronze Seed Badge', xpBonus: 25 },
  { days: 7, reward: 'Silver Sprout Badge', xpBonus: 50 },
  { days: 14, reward: 'Gold Sapling Badge', xpBonus: 100 },
  { days: 30, reward: 'Emerald Harvest Badge', xpBonus: 200 },
  { days: 60, reward: 'Diamond Greenhouse Badge', xpBonus: 500 },
  { days: 100, reward: 'Legendary Homestead Badge', xpBonus: 1000 },
];

/**
 * Check if a streak count hits a milestone
 */
export function getStreakMilestone(streakCount) {
  return STREAK_MILESTONES.find((m) => m.days === streakCount) || null;
}

/**
 * Level titles/ranks
 */
export function getLevelTitle(level) {
  if (level >= 50) return 'Homestead';
  if (level >= 40) return 'Greenhouse';
  if (level >= 30) return 'Orchard';
  if (level >= 20) return 'Farm';
  if (level >= 15) return 'Allotment';
  if (level >= 10) return 'Plot';
  if (level >= 7) return 'Garden';
  if (level >= 5) return 'Bed';
  if (level >= 3) return 'Patch';
  return 'Seedling';
}
